import weekdayAnswers from './../data/weekday/answers.json';
import weekdaySolutions from './../data/weekday/solutions.json';
import weekdayRoutings from './../data/weekday/routings.json';
import weekendAnswers from './../data/weekend/answers.json';
import weekendSolutions from './../data/weekend/solutions.json';
import weekendRoutings from './../data/weekend/routings.json';
import nightAnswers from './../data/night/answers.json';
import nightRoutings from './../data/night/routings.json';
import accessibleAnswers from './../data/accessible/answers.json';
import accessibleSolutions from './../data/accessible/solutions.json';
import accessibleRoutings from './../data/accessible/routings.json';
import transfers from './../data/transfers.json';

const weekdayAnswerSet = new Set(weekdayAnswers.map(a => a.join('-')));
const weekendAnswerSet = new Set(weekendAnswers.map(a => a.join('-')));
const nightAnswerSet = new Set(nightAnswers.map(a => a.join('-')));
const accessibleAnswerSet = new Set(accessibleAnswers.map(a => a.join('-')));

const ROUTES_WITH_NO_WEEKEND_SERVICE = ['B', 'W'];
const ROUTES_WITH_NO_NIGHT_SERVICE = ['B', 'C', 'W', 'GS'];
const GAME_EPOCH = new Date('January 29, 2022 00:00:00').valueOf();
export const NIGHT_GAMES = [350, 351];
const ACCESSIBLE_GAME = 793;
const DEKALB_AV_FLATBUSH_STOP = "R30";

const today = new Date();
const now = Date.now();

const isSimilarToAnswerTrain = (guess, index, trip, solution) => {
  let begin;
  let end;
  const answer = trip[index];
  const routings = todaysRoutings();
  switch (index) {
    case 0:
      begin = solution.origin;
      end = solution.first_transfer_arrival;
      break;
    case 1:
      begin = solution.first_transfer_departure;
      end = solution.second_transfer_arrival;
      break;
    default:
      begin = solution.second_transfer_departure;
      end = solution.destination;
  }

  const guessSubrouting = retrieveSubrouting(guess, routings, begin, end);

  if (!guessSubrouting) {
    return false;
  }

  const answerSubrouting = retrieveSubrouting(answer, routings, begin, end);

  const guessSubroutingInner = guessSubrouting.slice(1, guessSubrouting.length).filter(s => s !== DEKALB_AV_FLATBUSH_STOP);
  const answerSubroutingInner = answerSubrouting.slice(1, answerSubrouting.length).filter(s => s !== DEKALB_AV_FLATBUSH_STOP);

  if (guessSubroutingInner.every(s => answerSubroutingInner.includes(s)) || answerSubroutingInner.every(s => guessSubroutingInner.includes(s))) {
    return (guessSubrouting.includes(begin) && answerSubrouting.includes(begin)) || (guessSubrouting.includes(end) && answerSubrouting.includes(end));
  }

  return false;
}

const retrieveSubrouting = (train, routings, begin, end) => {
  let trainLookup;
  if (train === 'A') {
    if (routings['A1'].includes(begin) && routings['A1'].includes(end)) {
      trainLookup = 'A1';
    } else {
      trainLookup = 'A2';
    }
  } else {
    trainLookup = train;
  }

  const beginIndex = [begin, transfers[begin]].flat().filter(n => n).map(s => routings[trainLookup].indexOf(s)).find(i => i > -1);
  const endIndex = [end, transfers[end]].flat().filter(n => n).map(s => routings[trainLookup].indexOf(s)).find(i => i > -1);

  if (beginIndex == null || endIndex == null) {
    return;
  }

  if (beginIndex < endIndex) {
    return routings[trainLookup].slice(beginIndex, endIndex + 1);
  }
  return routings[trainLookup].slice(endIndex, beginIndex + 1);
}

export const isWeekend = [0, 6].includes(today.getDay());

export const routesWithNoService = () => {
  if (isNight) {
    return ROUTES_WITH_NO_NIGHT_SERVICE;
  }
  if (isWeekend) {
    return ROUTES_WITH_NO_WEEKEND_SERVICE;
  }
  return [];
}

export const isValidGuess = (guess) => {
  const flattenedGuess = guess.join('-');
  if (isNight) {
    return nightAnswerSet.has(flattenedGuess);
  }
  if (isAccessible) {
    return accessibleAnswerSet.has(flattenedGuess);
  }
  if (isWeekend) {
    return weekendAnswerSet.has(flattenedGuess);
  }
  return weekdayAnswerSet.has(flattenedGuess);
}

export const todayGameIndex = () => {
  return Math.floor(daysBetween(GAME_EPOCH, now));
}

const treatAsUTC = (date) => {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
  return result;
}

const daysBetween = (startDate, endDate) => {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / millisecondsPerDay;
}

export const isNight = NIGHT_GAMES.includes(todayGameIndex());
export const isAccessible = todayGameIndex() === ACCESSIBLE_GAME;

const todaysRoutings = () => {
  if (isNight) {
    return nightRoutings;
  }
  if (isAccessible) {
    return accessibleRoutings;
  }
  if (isWeekend) {
    return weekendRoutings;
  }
  return weekdayRoutings;
}

export const todaysTrip = () => {
  const index = todayGameIndex();
  if (isNight) {
    return nightAnswers[index % nightAnswers.length];
  }
  if (isAccessible) {
    return accessibleAnswers[index % accessibleAnswers.length];
  }
  if (isWeekend) {
    return weekendAnswers[index % weekendAnswers.length];
  }
  return weekdayAnswers[index % weekdayAnswers.length];
}

export const flattenedTodaysTrip = () => {
  return todaysTrip().join('-');
}

export const todaysSolution = () => {
  if (isNight) {
    return weekendSolutions[todaysTrip().join("-")];
  }
  if (isAccessible) {
    return accessibleSolutions[todaysTrip().join("-")];
  }
  if (isWeekend) {
    return weekendSolutions[todaysTrip().join("-")];
  }
  return weekdaySolutions[todaysTrip().join("-")];
}

export const getSolution = (trip) => {
  const key = trip.join('-');
  if (isNight) {
    return weekendSolutions[key];
  }
  if (isAccessible) {
    return accessibleSolutions[key];
  }
  if (isWeekend) {
    return weekendSolutions[key];
  }
  return weekdaySolutions[key];
}

const todaysAnswers = () => {
  if (isNight) return nightAnswers;
  if (isAccessible) return accessibleAnswers;
  if (isWeekend) return weekendAnswers;
  return weekdayAnswers;
}

export const getPracticeTrip = (options = {}) => {
  // options.includeWeekend, options.includeWeekday control pools
  const includeWeekend = options.includeWeekend;
  const includeWeekday = (options.includeWeekday === undefined) ? true : options.includeWeekday;

  let answersPool = [];
  if (includeWeekend && includeWeekday) {
    const combined = [...weekdayAnswers, ...weekendAnswers];
    const seen = new Set();
    answersPool = combined.filter(a => {
      const key = a.join('-');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } else if (includeWeekend) {
    // Defensive filter: weekend-only practice should never include no-weekend-service routes.
    answersPool = weekendAnswers
      .slice()
      .filter(answer => !answer.some(route => ROUTES_WITH_NO_WEEKEND_SERVICE.includes(route)));
  } else if (includeWeekday) {
    answersPool = weekdayAnswers.slice();
  } else {
    // fallback to todaysAnswers if none selected
    answersPool = todaysAnswers();
  }

  const dailyTripStr = flattenedTodaysTrip();
  const filtered = answersPool.filter(t => t.join('-') !== dailyTripStr);
  return filtered[Math.floor(Math.random() * filtered.length)];
}

export const getTripServiceType = (trip, options = {}) => {
  if (!trip) return 'weekday';
  const preferWeekday = options.preferWeekday === true;
  const preferWeekend = options.preferWeekend === true;
  const key = trip.join('-');
  const inWeekend = weekendAnswerSet.has(key);
  const inWeekday = weekdayAnswerSet.has(key);

  if (inWeekend && !inWeekday) return 'weekend';
  if (inWeekday && !inWeekend) return 'weekday';

  // Respect caller preference when a trip exists in both service datasets.
  if (inWeekend && inWeekday) {
    if (preferWeekday && !preferWeekend) return 'weekday';
    if (preferWeekend && !preferWeekday) return 'weekend';
  }

  // If a trip exists in both sets, prefer weekend label for display.
  return inWeekend ? 'weekend' : 'weekday';
}

export const isWinningGuess = (guess, tripOverride) => {
  return guess.join('-') === (tripOverride || todaysTrip()).join('-');
}

export const updateGuessStatuses = (guesses, setCorrectRoutes, setSimilarRoutes, setPresentRoutes, setAbsentRoutes, setSimilarRoutesIndexes, correctRoutes, similarRoutes, presentRoutes, absentRoutes, similarRoutesIndexes, tripOverride, solutionOverride) => {
  const correct = correctRoutes || [];
  let similar = similarRoutes || [];
  const present = presentRoutes || [];
  const absent = absentRoutes || [];
  const similarIndexes = similarRoutesIndexes || {};

  const trip = tripOverride || todaysTrip();
  const solution = solutionOverride || todaysSolution();

  guesses.forEach((guess) => {
    const remainingRoutes = [];
    const remainingGuessPositions = [];

    trip.forEach((routeId, index) => {
      if (guess[index] === routeId) {
        correct.push(routeId);
        Object.keys(similarIndexes).forEach((r) => {
          const s = similarIndexes[r];
          if (s.includes(index)) {
            similarIndexes[r] = s.filter(t => t !== index);
            if (similarIndexes[r].length === 0) {
              delete similarIndexes[r];
              similar = similar.filter(t => t !== r);
            }
          }
        })
      } else {
        remainingRoutes.push(routeId);
        remainingGuessPositions.push(index);

        if (isSimilarToAnswerTrain(guess[index], index, trip, solution)) {
          similar.push(guess[index]);
          if (similarIndexes[guess[index]] && !similarIndexes[guess[index]].includes(index)) {
            similarIndexes.push(index);
          } else if (!similarIndexes[guess[index]]) {
            similarIndexes[guess[index]] = [index];
          }
        }
      }
    });

    remainingGuessPositions.forEach((index) => {
      if (remainingRoutes.includes(guess[index])) {
        present.push(guess[index]);
      } else {
        absent.push(guess[index]);
      }
    });
  });

  setCorrectRoutes(correct);
  setSimilarRoutes(similar);
  setPresentRoutes(present);
  setAbsentRoutes(absent);
  setSimilarRoutesIndexes(similarIndexes);
}

export const checkGuessStatuses = (guess, tripOverride, solutionOverride) => {
  const trip = tripOverride || todaysTrip();
  const solution = solutionOverride || todaysSolution();
  const results = ['absent', 'absent', 'absent'];
  const remainingRoutes = [];
  const remainingGuessPositions = [];

  trip.forEach((routeId, index) => {
    if (guess[index] === routeId) {
      results[index] = 'correct';
    } else {
      remainingRoutes.push(routeId);
      remainingGuessPositions.push(index);
      if (isSimilarToAnswerTrain(guess[index], index, trip, solution)) {
        results[index] = 'similar';
      }
    }
  });

  remainingGuessPositions.forEach((index) => {
    if (remainingRoutes.includes(guess[index])) {
      results[index] = 'present';
    }
  });

  return results;
}
